import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import axios from "axios";
import { api_v3_get_matches_by_tournament_with_date } from "../../utils/constants";

const MATCH_FINISHED = "FINISHED";
const MATCH_REVIEWED = "REVIEWED";

function fmtYMD(d) {
  const date = new Date(d);
  return `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,"0")}-${String(date.getDate()).padStart(2,"0")}`;
}

const HomeTicker = ({ tournamentId }) => {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paused, setPaused] = useState(false);

  const handlePause = useCallback(() => setPaused(true), []);
  const handleResume = useCallback(() => setPaused(false), []);

  useEffect(() => {
    if (!tournamentId) return;
    const load = async () => {
      try {
        const res = await axios.get(api_v3_get_matches_by_tournament_with_date(tournamentId));
        if (res.status === 200) {
          setMatches(res.data);
        }
        setLoading(false);
      } catch (err) {
        setLoading(false);
      }
    };
    load();
  }, [tournamentId]);

  const today = new Date();
  const todayStr = fmtYMD(today);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowStr = fmtYMD(tomorrow);

  const nowMatches = matches.filter((m) => {
    if (!m.matchDate) return false;
    const d = fmtYMD(new Date(m.matchDate));
    return d === todayStr || d === tomorrowStr;
  });

  const formatTime = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleString("es-ES", {
        hour: "2-digit",
        minute: "2-digit",
        timeZone: "Europe/Madrid",
      });
    } catch {
      return "";
    }
  };

  if (loading || nowMatches.length === 0) return null;

  const items = [...nowMatches, ...nowMatches, ...nowMatches];

  return (
    <Wrapper>
      <Track
        onMouseEnter={handlePause}
        onMouseLeave={handleResume}
      >
        <Scroller style={{ animationPlayState: paused ? "paused" : "running" }}>
          {items.map((m, i) => {
            const isFinished = m.status === MATCH_FINISHED || m.status === MATCH_REVIEWED;
            const winnerA = isFinished &&
              ((m.playerAresult || 0) > (m.playerBresult || 0) || m.playerAaw === "AW");
            const winnerB = isFinished &&
              ((m.playerBresult || 0) > (m.playerAresult || 0) || m.playerBaw === "AW");

            const divShort = m.divisionName === "Espadas Largas" ? "Esp. Largas"
              : m.divisionName === "Hombre de Armas" ? "H. de Armas"
              : m.divisionName;

            return (
              <Tile key={`${m.matchId}-${i}`}>
                <Link to={`/match/${m.matchId}/detail`}>
                  <TimeWrap>
                    <Dot />
                    <Time>{formatTime(m.matchDate)}</Time>
                  </TimeWrap>
                  <Versus>
                    <Team className={winnerA ? "win" : ""}>{m.playerAusername}</Team>
                    {isFinished ? (
                      <Score className={winnerA ? "win" : ""}>
                        {m.playerAaw || m.playerAresult}
                      </Score>
                    ) : (
                      <Vs>VS</Vs>
                    )}
                    <Team className={winnerB ? "win" : ""}>{m.playerBusername}</Team>
                    {isFinished ? (
                      <Score className={winnerB ? "win" : ""}>
                        {m.playerBaw || m.playerBresult}
                      </Score>
                    ) : null}
                  </Versus>
                  {m.divisionName && <Tag>{divShort}</Tag>}
                </Link>
              </Tile>
            );
          })}
        </Scroller>
      </Track>
    </Wrapper>
  );
};

const Wrapper = styled.section`
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  overflow: hidden;
  position: relative;

  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    width: 60px;
    z-index: 2;
    pointer-events: none;
  }

  &::before {
    left: 0;
    background: linear-gradient(90deg, var(--v-canvas), transparent);
  }

  &::after {
    right: 0;
    background: linear-gradient(90deg, transparent, var(--v-canvas));
  }
`;

const Track = styled.div`
  background: var(--v-surface);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 20px;
  padding: 0.75rem 0;
  overflow: hidden;
`;

const Scroller = styled.div`
  display: flex;
  gap: 1rem;
  width: max-content;
  animation: tickerScroll 80s linear infinite;

  @keyframes tickerScroll {
    0% {
      transform: translateX(0);
    }
    100% {
      transform: translateX(-33.333%);
    }
  }
`;

const Tile = styled.div`
  flex-shrink: 0;

  a {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.4rem 1rem;
    border-radius: 12px;
    border: 1px solid rgba(255, 255, 255, 0.2);
    background: rgba(255, 255, 255, 0.08);
    text-decoration: none;
    color: inherit;
    transition: border-color var(--v-transition), background var(--v-transition);

    &:hover {
      border-color: var(--v-mint);
      border-color: rgba(60, 255, 208, 0.3);
      background: rgba(60, 255, 208, 0.06);
    }
  }
`;

const TimeWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  min-width: 4rem;
`;

const Dot = styled.div`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--v-mint);
  flex-shrink: 0;
`;

const Time = styled.span`
  font-family: var(--v-font-mono);
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: var(--v-secondary);
  white-space: nowrap;
`;

const Versus = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
`;

const Team = styled.span`
  font-family: var(--v-font-ui);
  font-size: 13px;
  font-weight: 500;
  color: var(--v-white);
  white-space: nowrap;

  &.win {
    color: var(--v-mint);
    font-weight: 600;
  }
`;

const Vs = styled.span`
  font-family: var(--v-font-mono);
  font-size: 9px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--v-secondary);
  opacity: 0.4;
`;

const Score = styled.span`
  font-family: var(--v-font-mono);
  font-size: 13px;
  font-weight: 700;
  color: var(--v-secondary);
  min-width: 1.2rem;
  text-align: center;

  &.win {
    color: var(--v-mint);
  }
`;

const Tag = styled.span`
  font-family: var(--v-font-mono);
  font-size: 8px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: var(--v-gold-bright);
  background: rgba(201, 160, 78, 0.1);
  padding: 1px 6px;
  border-radius: 8px;
  white-space: nowrap;
`;

export default HomeTicker;
